import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './components/AuthProvider';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import CDNManagement from './pages/CDNManagement';
import MCPIntegration from './pages/MCPIntegration';
import Experiments from './pages/Experiments';
import DynamicContent from './pages/DynamicContent';
import Settings from './pages/Settings';
import Login from './pages/Login';
import { ToastProvider } from './components/ui';
import OperationsOverview from './pages/OperationsOverview';
import FraudReview from './pages/FraudReview';
import OperationsQueues from './pages/OperationsQueues';
import ModerationContainer from './pages/ModerationContainer';
import DocumentReview from './pages/DocumentReview';
import Organizations from './pages/Organizations';
import CharityVerificationContainer from './pages/CharityVerificationContainer';
import Support from './pages/Support';
import Approvals from './pages/Approvals';
import Broadcasts from './pages/Broadcasts';
import ReferralCodes from './pages/ReferralCodes';
import WebsiteLeads from './pages/WebsiteLeads';
import Compliance from './pages/Compliance';
import UserEnforcement from './pages/UserEnforcement';
import AccountDeletionRequests from './pages/AccountDeletionRequests';
import AuditRetention from './pages/AuditRetention';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-text">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Get basename from the base path (remove trailing slash for router)
const basename = import.meta.env.BASE_URL.replace(/\/$/, '') || '/';

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter basename={basename}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="experiments" element={<Experiments />} />
                <Route path="dynamic-content" element={<DynamicContent />} />
                <Route path="cdn" element={<CDNManagement />} />
                <Route path="mcp" element={<MCPIntegration />} />
                <Route path="settings" element={<Settings />} />
                <Route path="operations" element={<OperationsOverview />} />
                <Route path="operations/queues" element={<OperationsQueues />} />
                <Route path="fraud-review" element={<FraudReview />} />
                <Route path="moderation" element={<ModerationContainer />} />
                <Route path="enforcement" element={<UserEnforcement />} />
                <Route path="document-review" element={<DocumentReview />} />
                <Route path="organizations" element={<Organizations />} />
                <Route path="charity-verifications" element={<CharityVerificationContainer />} />
                <Route path="support" element={<Support />} />
                <Route path="approvals" element={<Approvals />} />
                <Route path="broadcasts" element={<Broadcasts />} />
                <Route path="referral-codes" element={<ReferralCodes />} />
                <Route path="website-leads" element={<WebsiteLeads />} />
                <Route path="deletion-requests" element={<AccountDeletionRequests />} />
                <Route path="compliance" element={<Compliance />} />
                <Route path="audit-retention" element={<AuditRetention />} />
              </Route>
              {/* Catch-all route - redirect to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}

export default App;
